import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { MentorsDTO } from '../../../../../apis/api.assignation'
import { useHRExpertMentors } from '../../../../../hooks/useHRExpertMentors'
import { useProfileStatus } from '../../../../../hooks/useProfileStatus'
import Modal from 'react-modal'
import styles from './ModalExpertMentorStatus.module.css'
import { colors } from '../../../../../styles/globals/_variables'
import { useLocation } from 'wouter'
import { useTeam } from '../../../Hooks/useTeam'

export const ModalExpertMentorStatus = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean
    onClose?: (value: boolean) => void
}) => {
    const { t } = useTranslation('teamMonitor')
    const { mentors, refetch, isFetching } = useHRExpertMentors()

    return (
        <Modal isOpen={isOpen} style={modalStyles} onRequestClose={() => onClose?.(false)}>
            <FontAwesomeIcon
                icon={['fas', 'times']}
                onClick={() => onClose?.(false)}
                className={styles.closeIcon}
            />
            <section className={styles.modalSection}>
                <header>
                    <h4>{t('Expert Mentors')}</h4>
                </header>
                <main className={styles.profileList}>
                    <Section
                        title={t('Mentors available')}
                        mentors={mentors?.availables}
                        icon={['fas', 'circle-check']}
                        iconColor={colors.positiveGreen}
                        isFetching={isFetching}
                    />
                    <Section
                        title={t('Mentors busy')}
                        mentors={mentors?.busy}
                        icon={['fas', 'clock']}
                        iconColor={colors.eggColor}
                        isFetching={isFetching}
                    />
                    <Section
                        title={t('Mentors disconnected')}
                        mentors={mentors?.unavailable}
                        icon={['fas', 'ban']}
                        iconColor={colors.salmon}
                        isFetching={isFetching}
                    />
                </main>
                <footer>
                    <button onClick={() => refetch()} className="outlined" disabled={isFetching}>
                        {t('Refresh')}
                    </button>
                </footer>
            </section>
        </Modal>
    )
}

const Section = ({
    title,
    mentors,
    icon,
    iconColor,
    isFetching,
}: {
    title: string
    mentors?: MentorsDTO[]
    icon: IconProp
    iconColor: string
    isFetching?: boolean
}) => {
    const { t } = useTranslation('teamMonitor')
    if (!mentors) return null

    return (
        <section>
            <header>
                <h5>
                    <FontAwesomeIcon icon={icon} style={{ marginRight: 10, color: iconColor }} />
                    {title}: {isFetching ? '-' : mentors.length}
                </h5>
            </header>
            <main>
                {mentors.length === 0 && <div className={styles.noMentor}>{t('No mentors')}</div>}
                {mentors.map((mentor) => (
                    <ProfileItem
                        key={mentor.profileId}
                        profileId={mentor.profileId}
                        teamNumber={mentor.teamNumber}
                    />
                ))}
            </main>
        </section>
    )
}

const ProfileItem = ({ profileId, teamNumber }: { profileId: string; teamNumber?: number }) => {
    const [, setLocation] = useLocation()
    const { courseID } = useTeam()
    const { t } = useTranslation('teamMonitor')
    const { data } = useProfileStatus(profileId)
    return (
        <div className={styles.profileItem}>
            <span>{data?.displayName || profileId}</span>
            {teamNumber && (
                <button
                    className="link"
                    onClick={() =>
                        setLocation(
                            `/admin/team/${courseID}?searchType=teams&searchQuery=${teamNumber}`
                        )
                    }
                >
                    {t('Team')} {teamNumber}
                </button>
            )}
        </div>
    )
}

export const modalStyles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'var(--darkGreyEgg)',
        zIndex: 600001,
    },
    content: {
        inset: '50%',
        border: 0,
        background: 'rgb(255, 255, 255)',
        borderRadius: '8px',
        outline: 'none',
        overflow: 'hidden',
        padding: 20,
        width: 'fit-content',
        height: 'fit-content',
        transform: 'translate(-50%, -50%)',
    },
} as const
